/* Tour "forecast-objectives" — apprend à ajuster un montant à la main directement
   dans une cellule du tableau (saisie d'objectifs).

   Réutilise le bac à sable du tuto "forecast-formulas" (seed au démarrage,
   cleanup à la sortie) pour ne jamais toucher aux vraies données. */

import AppBus from '@/bus/app-bus';
import {seedSandbox, cleanupSandbox, readSandbox} from './forecast-formulas';
import {isTourCompleted} from './onboarding-state';
import './types';

// Garde-fou : Entrée ne doit avancer QU'UNE fois (saisie → flèche). Une fois sur
// l'étape flèche, on ignore Entrée (l'utilisateur doit cliquer la flèche).
let advancedToArrow = false;

/** Entrée à l'étape saisie : on AVANCE vers l'étape flèche (édition ouverte),
 *  sans valider la cellule. CellInput émet `tour:objectif-next`. */
function onObjectifNext() {
  if (advancedToArrow) return;
  if (window.FygrTour?.isActive?.()) {
    advancedToArrow = true;
    window.FygrTour.next();
  }
}

const SPAN_ID = 'tour-objectif-span';

function removeExtendSpan() {
  document.getElementById(SPAN_ID)?.remove();
}

/** Clic sur la flèche orange : submit(true) applique le montant à tous les mois,
 *  CellInput émet `tour:objectif-validated`. On rejoue alors le « spotlight qui
 *  s'étend sur toute la ligne » (ré-ancrage animé du halo sur la ligne du bac à
 *  sable). On NE termine PAS : le bouton « Terminer » de la bulle laisse
 *  l'utilisateur choisir de quitter. */
function onObjectifValidated() {
  if (!window.FygrTour?.isActive?.()) return;
  // L'ancre objectif-cell persiste après validation (liée au bac à sable, pas à
  // l'édition) → on en déduit la ligne à surligner.
  const cell = document
    .querySelector('[data-tour-step="objectif-cell"]')
    ?.closest('td');
  const row = cell?.closest('tr');
  if (!row || !cell) return;
  // On ne surligne QUE la partie prévisionnelle : de la cellule éditée jusqu'au
  // bout de la ligne (les mois passés / réalisés à gauche restent hors halo).
  const rowRect = row.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();
  const left = cellRect.left;
  const width = rowRect.right - cellRect.left;
  const span = document.getElementById(SPAN_ID) || document.createElement('div');
  span.id = SPAN_ID;
  (span as HTMLElement).style.cssText = [
    'position:fixed',
    `top:${rowRect.top}px`,
    `left:${left}px`,
    `width:${width}px`,
    `height:${rowRect.height}px`,
    'pointer-events:none',
    'z-index:1',
  ].join(';');
  if (!span.parentNode) document.body.appendChild(span);
  window.FygrTour.retarget?.(span);
}

/** Cible dynamique de l'étape « modifier un objectif ». Le moteur re-résout à
 *  chaque tick → on en profite pour RE-MARQUER le bac à sable (set-sandbox +
 *  expand) jusqu'à ce que la cellule `objectif-cell` existe vraiment. Évite le
 *  `Target not found` quand la ligne tarde à se monter / se marquer. */
function sandboxCellSelector(): string {
  const ref = readSandbox();
  if (ref) {
    AppBus.$emit('tour:set-sandbox', ref.subId);
    AppBus.$emit('tour:expand-category', ref.parentId);
  }
  return '[data-tour-step="objectif-cell"]';
}

/** Préchauffe la vue tableau + la grille PENDANT l'écran d'intro : on bascule en
 *  vue tableau (monte CashTable) puis, une fois monté, on déplie le bac à sable et
 *  on recharge la donnée. Ainsi, au clic « Passer en vue tableau », tout est déjà
 *  chargé → quasi instantané (le refetch réseau a eu lieu en arrière-plan). */
function warmTable() {
  AppBus.$emit('tour:dashboard-table');
  const ref = readSandbox();
  AppBus.$emit('tour:set-sandbox', ref?.subId);
  // CashTable se monte avec la bascule : on attend un peu avant expand + reload.
  setTimeout(() => {
    AppBus.$emit('tour:expand-category', ref?.parentId);
    AppBus.$emit('tour:reload-table');
  }, 700);
}

/** Au clic de l'étape 1 : on (re)bascule en vue tableau + on déplie le bac à
 *  sable en rafale. Pas de reload ici : la grille a déjà été préchauffée à
 *  l'intro (warmTable), donc l'ancre objectif-cell est déjà prête. */
function goToTableAndReveal() {
  AppBus.$emit('tour:dashboard-table');
  const ref = readSandbox();
  AppBus.$emit('tour:set-sandbox', ref?.subId);
  const parentId = ref?.parentId;
  let tries = 0;
  const tick = () => {
    AppBus.$emit('tour:expand-category', parentId);
    if (++tries < 6) setTimeout(tick, 400);
  };
  tick();
}

window.FygrTour.register('forecast-objectives', {
  title: 'Saisir un objectif',
  intro: {
    title: 'Saisissez un objectif',
    body: 'Le moyen le plus direct de construire votre prévisionnel : ajustez le montant de n’importe quelle cellule du tableau pour fixer un objectif.',
    note: 'Vous vous exercez sur une catégorie temporaire, supprimée automatiquement à la fin : vos données réelles ne sont pas modifiées.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Modules ».',
  },
  onStart: async () => {
    advancedToArrow = false;
    AppBus.$on('tour:objectif-next', onObjectifNext);
    AppBus.$on('tour:objectif-validated', onObjectifValidated);
    await seedSandbox();
    // Préchauffe la grille pendant que l'utilisateur lit l'écran d'intro.
    warmTable();
  },
  onExit: () => {
    AppBus.$off?.('tour:objectif-next', onObjectifNext);
    AppBus.$off?.('tour:objectif-validated', onObjectifValidated);
    removeExtendSpan();
    cleanupSandbox();
  },
  steps: [
    {
      target: '[data-tour-step="dash-view-table"]',
      title: 'Passez en vue tableau',
      body: 'La saisie d’objectifs se fait dans la vue tableau. Cliquez sur l’icône tableau pour l’afficher.',
      placement: 'bottom',
      clickToAdvance: true,
      nextDelay: 500,
      onNext: goToTableAndReveal,
    },
    {
      target: sandboxCellSelector,
      title: 'Modifier un objectif',
      body: 'Sur la ligne « Exemple Okimia », survolez une cellule de prévision : l’icône « Modifier l’objectif » apparaît. Cliquez dessus.',
      placement: 'top',
      waitTimeout: 8000,
      // Avance dès que la saisie inline s’ouvre.
      advanceWhen: '[data-tour-step="objectif-input"]',
      // Neutralise l'icône commentaire (popup « Détails ») — seul le crayon doit
      // être cliquable.
      blockWithin: '.adj-icon.commented',
    },
    {
      target: '[data-tour-step="objectif-input"]',
      title: 'Saisissez votre objectif',
      body: 'Tapez le montant souhaité : un montant négatif correspond à une sortie, un montant positif à une entrée. Appuyez sur Entrée (ou « Suivant ») pour continuer.',
      placement: 'top',
      // Entrée OU « Suivant » mènent à l'étape flèche orange, sans fermer
      // l'édition (Entrée est interceptée par CellInput → tour:objectif-next).
      clickToAdvance: false,
      blockWithin: '.adj-icon.commented',
      waitTimeout: 3000,
    },
    {
      // Étape flèche orange : appliquer le montant à tous les mois suivants. Le
      // clic déclenche submit(true) → tour:objectif-validated → fin du guide.
      target: '[data-tour-step="objectif-submit"]',
      title: 'Appliquer aux mois suivants',
      body: 'Cliquez sur la flèche orange pour appliquer ce montant à cette cellule et à tous les mois suivants.',
      placement: 'top',
      clickToAdvance: false,
      waitTimeout: 4000,
    },
  ],
  celebration: {
    title: 'Objectif enregistré',
    body: 'La catégorie d’exemple va être supprimée. Pour aller plus loin :',
    actionPanel: {
      title: 'Pour aller plus loin',
      body: 'Affinez votre prévisionnel central.',
      actions: [
        {
          label: 'Générer une prévision par IA',
          body: 'Okimia analyse votre historique et propose une formule.',
          icon: 'auto_awesome',
          // On ne le repropose pas si l'utilisateur en vient déjà (état back).
          hideIf: () => isTourCompleted('forecast-ai'),
          onSelect: () => {
            // Navigation complète (et pas start en place) : on repart d'un état
            // client propre (sinon la ligne fantôme du bac à sable peut casser le
            // tuto IA). autoStart relance le guide via ?tour=.
            window.location.assign('/dashboard?tour=forecast-ai&from=recap');
          },
        },
        {
          label: 'Intégrer vos factures',
          body: 'Projetez vos factures clients et fournisseurs.',
          icon: 'receipt_long',
          // Enchaîne sur le tuto factures (échéances), lancé via ?tour= au
          // montage de la page /invoices.
          href: '/invoices?tour=invoices-overview&from=recap',
        },
      ],
    },
  },
});

export {};
